if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.useRebuyCart = !!window.Rebuy || !!document.querySelector('#rebuy-smart-cart-wrapper, #rebuy-smart-cart');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const submittedProductFormId = this.form.getAttribute('id') || '';
        this.stripInactiveWristStrapFields(formData, submittedProductFormId);
        this.normalizeGiftWidgetBundleFields(formData, submittedProductFormId);
        const complementaryVariantIds = [
          ...new Set(formData.getAll('complementary_variant_ids[]').filter((variantId) => variantId)),
        ];
        formData.delete('complementary_variant_ids[]');
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const addToCartPromise =
          complementaryVariantIds.length > 0
            ? this.addWithComplementaryProducts(formData, complementaryVariantIds)
            : fetch(`${routes.cart_add_url}`, config).then((response) => response.json());

        addToCartPromise
          .then((response) => {
            if (response.status) {
              document.dispatchEvent(
                new CustomEvent('product-form:submit-error', {
                  detail: { productFormId: submittedProductFormId, response },
                })
              );
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            document.dispatchEvent(
              new CustomEvent('product-form:submit-success', {
                detail: { productFormId: submittedProductFormId, response },
              })
            );

            if (!this.cart) {
              document.dispatchEvent(
                new CustomEvent('wrist-strap:reset', {
                  detail: {
                    source: 'product-form',
                    standaloneOnly: true,
                    clearMirroredGiftWidget: true,
                    productFormId: submittedProductFormId,
                  },
                })
              );
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  if (!this.useRebuyCart) {
                    setTimeout(() => {
                      this.cart.renderContents(response);
                    });
                  }
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else if (!this.useRebuyCart) {
              this.cart.renderContents(response);
            }

            document.dispatchEvent(
              new CustomEvent('wrist-strap:reset', {
                detail: {
                  source: 'product-form',
                  standaloneOnly: true,
                  clearMirroredGiftWidget: true,
                  productFormId: submittedProductFormId,
                },
              })
            );
            this.refreshGiftPriceDisplay(submittedProductFormId);
          })
          .catch((e) => {
            console.error(e);
            document.dispatchEvent(
              new CustomEvent('product-form:submit-error', {
                detail: { productFormId: submittedProductFormId, error: e },
              })
            );
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      addWithComplementaryProducts(formData, variantIds = []) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          const propertyName = key.match(/^properties\[(.+)\]$/)?.[1];
          if (propertyName) properties[propertyName] = value;
        }

        const mainItem = {
          id: parseInt(formData.get('id'), 10),
          quantity: parseInt(formData.get('quantity'), 10) || 1,
          properties,
        };
        if (formData.get('selling_plan')) mainItem.selling_plan = formData.get('selling_plan');

        const items = variantIds
          .map((variantId) => parseInt(variantId, 10))
          .filter((variantId) => Number.isInteger(variantId) && variantId > 0)
          .map((variantId) => ({ id: variantId, quantity: 1 }));
        items.push(mainItem);

        const requestConfig = fetchConfig('javascript');
        requestConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
        requestConfig.headers['Content-Type'] = 'application/json';

        const payload = { items };
        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        requestConfig.body = JSON.stringify(payload);

        return fetch(`${routes.cart_add_url}`, requestConfig).then((response) => response.json());
      }

      stripInactiveWristStrapFields(formData, productFormId) {
        if (!productFormId) return;

        const standaloneWidget = document.querySelector(
          `[data-wrist-strap-widget][data-standalone="true"][data-product-form-id="${productFormId}"]`
        );
        const giftWidgetWrist = document.querySelector(
          `[data-wrist-strap-widget]:not([data-standalone="true"])[data-product-form-id="${productFormId}"]`
        );
        const giftWidgetButton = giftWidgetWrist?.querySelector('[data-wrist-strap-trigger]');

        const standaloneSelected =
          !!standaloneWidget &&
          standaloneWidget.classList.contains('is-selected') &&
          (standaloneWidget.dataset.selectedVariantId || '') !== '';

        const giftWidgetSelected = !!giftWidgetButton && giftWidgetButton.classList.contains('is-remove');

        const wristFieldNames = [
          'properties[_wristStrapCharges]',
          'properties[_wristId]',
          'properties[_wristStrapColor]',
          'properties[_isStrapDiscount]',
          'properties[_strapDiscount]',
          'properties[_isWristStrap_dev]',
        ];

        wristFieldNames.forEach((fieldName) => formData.delete(fieldName));

        if (standaloneSelected) {
          this.appendWristStrapFields(formData, standaloneWidget, wristFieldNames);
          return;
        }

        if (giftWidgetSelected) {
          this.appendWristStrapFields(formData, giftWidgetWrist, wristFieldNames);
        }
      }

      normalizeGiftWidgetBundleFields(formData, productFormId) {
        if (!productFormId) return;

        const sharedFieldNames = ['properties[_pc_pricing_ref]', 'properties[_bundle_id]'];
        const enabledSharedInputs = Array.from(
          document.querySelectorAll(
            sharedFieldNames
              .map(
                (fieldName) =>
                  `[form="${CSS.escape(productFormId)}"][name="${CSS.escape(fieldName)}"]:not([disabled])`
              )
              .join(', ')
          )
        );

        if (!enabledSharedInputs.length) return;

        const referenceTime = `${Date.now()}`;
        const bundleId = window.__esCreateBundleId
          ? window.__esCreateBundleId()
          : `${Date.now()}${Math.floor(Math.random() * 1000000)
              .toString()
              .padStart(6, '0')}`;

        enabledSharedInputs.forEach((input) => {
          if (input.name === 'properties[_pc_pricing_ref]') {
            input.value = referenceTime;
          }

          if (input.name === 'properties[_bundle_id]') {
            input.value = bundleId;
          }
        });

        sharedFieldNames.forEach((fieldName) => formData.delete(fieldName));
        formData.append('properties[_pc_pricing_ref]', referenceTime);
        formData.append('properties[_bundle_id]', bundleId);
      }

      appendWristStrapFields(formData, widget, fieldNames = []) {
        if (!widget) return;

        fieldNames.forEach((fieldName) => {
          const input = widget.querySelector(`[name="${CSS.escape(fieldName)}"]`);
          if (!input) return;

          const value = input.value;
          if (value === '') return;

          formData.append(fieldName, value);
        });
      }

      refreshGiftPriceDisplay(productFormId) {
        if (!productFormId) return;

        const widget = document
          .querySelector(`[data-product-form-id="${productFormId}"][data-wrist-strap-widget]`)
          ?.closest('gift-widget');
        if (widget && typeof widget.handlePrice === 'function') {
          widget.handlePrice();
          return;
        }

        const giftPriceEl = document.querySelector('[js-gift-price]');
        if (giftPriceEl && !document.querySelector('[js-combo-add-btn].is-remove')) {
          giftPriceEl.textContent = '';
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
